import { useEffect, useState } from 'react';
import { lapsOf } from './store';
import {
  TEAL, AMBER, GREEN, DIM, LINE, INK, def, secs, clock, Def, Box, Actions,
  lab, hint, bigInput, wide, quiet, stepsBox, stepRow, nextStep, num, stepper, sect, smallBtn, chip,
} from './recorderUi';

// Assembly, routines and time at the machine on the phone (the director, 7 Oct
// and 3 Nov 2026). Assembly is by hand, so the app cannot see it: the person
// taps. A batch is one clock; step by step and a routine are a clock per step,
// the steps coming from Build steps or the confirmed routine, or named at the
// end where there are none yet. Getting ready and pauses stay out of the work.

const PAUSE_WHY = ['Waiting for parts', 'A machine needs me', 'Break', 'Something else'];
const FALLBACK_UNITS = [{ key: 'product', label: '1 product', pieces: 1 }, { key: 'other', label: 'Another number', pieces: null }];

// ── before it starts ────────────────────────────────────────────────────────
export function AssemblyStart({ task, defs, onStart }) {
  const plan = task.plan || {};
  const units = (task.stock_item_id ? plan.step_units : plan.units) || FALLBACK_UNITS;
  const [unitKey, setUnitKey] = useState(() => (units.some(u => u.key === task.last_unit) ? task.last_unit : units[0].key));
  const unit = units.find(u => u.key === unitKey) || units[0];
  const byStep = task.method === 'steps';
  const first = byStep ? (plan.steps || [])[0] : null;
  const oneStep = task.stock_item_id ? (plan.steps || []).find(s => s.item_id === task.stock_item_id) : null;
  const go = (mark) => onStart(task, mark, { unit: { key: unit.key, label: unit.label, pieces: unit.pieces },
    ...(mark === 'start' && first ? { name: first.name, item_id: first.item_id } : {}) });
  return (
    <>
      <div style={{ margin: '4px 16px 10px', fontSize: 13, color: DIM }}>
        The director asked for <b style={{ color: INK }}>{byStep ? 'step by step' : 'a batch'}</b>
        {byStep ? ': tap the moment each next step starts.' : ': work as usual, then say how many and how many people.'}
      </div>
      {oneStep && oneStep.how && <div style={{ margin: '0 16px 10px', fontSize: 12.5, color: DIM }}>{oneStep.how}</div>}
      <div style={{ margin: '4px 14px' }}>
        <div style={lab}>How much are you making?</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {units.map(u => (
            <button key={u.key} onClick={() => setUnitKey(u.key)} aria-pressed={u.key === unitKey}
              style={{ ...stepRow, cursor: 'pointer', background: u.key === unitKey ? 'rgba(20,184,166,0.12)' : 'none', borderColor: u.key === unitKey ? TEAL : LINE, fontWeight: u.key === unitKey ? 700 : 500 }}>
              {u.label}
              {u.key === task.last_unit && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: DIM }}>last time</span>}
            </button>
          ))}
        </div>
        <div style={hint}>You say how many were really finished at the end.</div>
      </div>
      <Def d={def(defs, 'assembly', 'start')} />
      <button onClick={() => go('start')} style={wide}>Start: first part picked up</button>
      <Def d={def(defs, 'assembly', 'prep')} />
      <button onClick={() => go('prep')} style={quiet}>Start with getting ready</button>
    </>
  );
}

export function RoutineStart({ task, defs, onStart }) {
  const steps = (task.routine && task.routine.steps) || [];
  const confirmed = task.routine && task.routine.status === 'confirmed';
  return (
    <>
      <div style={{ margin: '4px 16px 10px', fontSize: 13, color: DIM }}>
        {confirmed
          ? <>{steps.length} steps. Tap the moment each next one starts; if you do something that is not on the list, add it.</>
          : <>Do it as you always do, and tap <b style={{ color: INK }}>Next step</b> each time something new starts. You name the steps at the end.</>}
      </div>
      {confirmed && <div style={stepsBox}>{steps.map((s, i) => <div key={s.name} style={stepRow}><span style={num}>{i + 1}</span>{s.name}</div>)}</div>}
      <Def d={def(defs, 'routine', 'start')} />
      <button onClick={() => onStart(task, 'start', confirmed && steps[0] ? { name: steps[0].name } : {})} style={wide}>Start{confirmed && steps[0] ? `: ${steps[0].name}` : ''}</button>
    </>
  );
}

// ── while it runs ───────────────────────────────────────────────────────────
function Pauser({ state, defs, onTap }) {
  const [asking, setAsking] = useState(false);
  if (state.paused) {
    return (
      <>
        <Box tone={AMBER}>Paused{state.paused.reason ? `: ${state.paused.reason}` : ''}. The clock on the work has stopped.</Box>
        <button onClick={() => onTap('resume')} style={wide}>Carry on</button>
      </>
    );
  }
  if (asking) {
    return (
      <div style={{ margin: '6px 14px' }}>
        <Def d={def(defs, 'assembly', 'pause')} />
        <div style={lab}>Why are you stopping?</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {PAUSE_WHY.map(w => <button key={w} onClick={() => { setAsking(false); onTap('pause', { reason: w }); }} style={chip}>{w}</button>)}
          <button onClick={() => setAsking(false)} style={{ ...chip, color: DIM }}>Not stopping</button>
        </div>
      </div>
    );
  }
  return <button onClick={() => setAsking(true)} style={quiet}>Pause</button>;
}

// Stopped: how many, how many people, and what the laps were.
function FinishForm({ running, task, defs, onSave, onCarryOn, busy }) {
  const state = lapsOf(running.marks, Date.parse(running.stopped));
  const pieces = state.unit && state.unit.pieces;
  const isRoutine = task.kind === 'routine';
  // A batch is one clock: only step by step and routines have steps to name.
  const byStep = isRoutine || task.method === 'steps';
  const [count, setCount] = useState(pieces ? String(pieces) : (isRoutine ? String(state.rounds) : ''));
  const [people, setPeople] = useState(1);
  const [names, setNames] = useState(() => state.laps.map(l => l.name));
  const [own, setOwn] = useState(false);
  const [counts, setCounts] = useState({});
  const unnamed = byStep && state.laps.some(l => !l.name);
  const keyOf = (l, i) => (l.item_id != null ? `i${l.item_id}` : `n${String(names[i] || '').trim().toLowerCase()}`);
  const ready = (isRoutine || Number(String(count).replace(',', '.')) > 0) && (!byStep || names.every(n => String(n).trim()));
  return (
    <>
      <div style={{ textAlign: 'center', color: DIM, fontSize: 13, padding: '4px 16px 8px' }}>The clock has stopped at {clock(state.workSecs * 1000)} of work.</div>
      {task.kind === 'assembly' && <Def d={def(defs, 'assembly', task.stock_item_id ? 'finish_step' : 'finish')} />}
      {unnamed && (
        <div style={{ margin: '6px 14px' }}>
          <div style={lab}>Name the steps, in the words you use. The same name twice is one step.</div>
          {state.laps.map((l, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: DIM, marginBottom: 4 }}>Step {i + 1} · {secs(l.secs)}</div>
              <input aria-label={`Name of step ${i + 1}`} value={names[i]} onChange={e => setNames(n => n.map((x, j) => (j === i ? e.target.value : x)))}
                placeholder="What was this step?" style={{ ...bigInput, fontSize: 16, fontFamily: 'system-ui, sans-serif' }} />
              {names.slice(0, i).some(Boolean) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                  {[...new Set(names.slice(0, i).filter(Boolean))].map(w => <button key={w} onClick={() => setNames(n => n.map((x, j) => (j === i ? w : x)))} style={{ ...chip, padding: '5px 9px', fontSize: 12 }}>{w}</button>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ margin: '8px 14px' }}>
        <label htmlFor="rec-count" style={lab}>{task.kind === 'assembly' ? 'How many were finished?' : 'How many pieces? (1 if it is not by the piece)'}</label>
        <input id="rec-count" inputMode="decimal" value={count} onChange={e => setCount(e.target.value)} placeholder="0" style={bigInput} />
        {state.unit && state.unit.pieces ? <div style={hint}>You planned {state.unit.label}: change it if you finished fewer or more.</div> : null}
      </div>
      <div style={{ margin: '8px 14px' }}>
        <div style={lab}>How many people worked on it?</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button aria-label="One fewer person" onClick={() => setPeople(v => Math.max(1, v - 1))} style={stepper}>−</button>
          <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 28, fontWeight: 700, minWidth: 40, textAlign: 'center' }}>{people}</span>
          <button aria-label="One more person" onClick={() => setPeople(v => v + 1)} style={stepper}>+</button>
        </div>
      </div>
      {byStep && state.laps.length > 1 && (
        <div style={{ margin: '8px 14px' }}>
          <button onClick={() => setOwn(o => !o)} aria-expanded={own} style={{ ...smallBtn, fontSize: 13 }}>{own ? 'Every step made the same number' : 'A step made a different number?'}</button>
          {own && state.laps.map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 13 }}>
              <span style={{ flex: 1, minWidth: 0 }}>{names[i] || `Step ${i + 1}`}</span>
              <input aria-label={`How many in ${names[i] || `step ${i + 1}`}`} inputMode="decimal" value={counts[keyOf(l, i)] || ''} placeholder={count || '0'}
                onChange={e => setCounts(c => ({ ...c, [keyOf(l, i)]: e.target.value }))}
                style={{ ...bigInput, width: 100, fontSize: 16, padding: 8 }} />
            </div>
          ))}
        </div>
      )}
      <button disabled={busy || !ready} onClick={() => onSave({ count, people, unit: state.unit, names, step_counts: counts })} style={{ ...wide, opacity: ready ? 1 : 0.5 }}>Save</button>
      <button onClick={onCarryOn} style={quiet}>Not finished yet: carry on</button>
    </>
  );
}

function useTick() {
  const [, set] = useState(0);
  useEffect(() => { const iv = setInterval(() => set(n => n + 1), 1000); return () => clearInterval(iv); }, []);
}

export function BatchRun({ running, task, defs, ctl }) {
  useTick();
  const state = lapsOf(running.marks);
  if (running.stopped) return <FinishForm running={running} task={task} defs={defs} busy={ctl.busy} onCarryOn={ctl.carryOn} onSave={ctl.saveLaps} />;
  const unitSaid = state.unit && state.unit.label;
  return (
    <>
      <div style={{ textAlign: 'center', fontFamily: 'ui-monospace, monospace', fontSize: 44, fontWeight: 700, padding: '8px 0 0', fontVariantNumeric: 'tabular-nums' }}>
        {clock((state.prep ? Date.now() - Date.parse(running.marks[0].at) : state.workSecs * 1000))}
      </div>
      <div style={{ textAlign: 'center', color: DIM, fontSize: 13, padding: '2px 16px 8px' }}>
        {state.prep ? 'Getting ready' : `Working${unitSaid ? ` · ${unitSaid}` : ''}`}
      </div>
      {state.prep
        ? <button onClick={() => ctl.tap('start')} style={wide}>First part picked up</button>
        : <Pauser state={state} defs={defs} onTap={ctl.tap} />}
      {!state.prep && !state.paused && <button onClick={ctl.stop} style={wide}>Finish</button>}
      <Actions running={running} onUndo={ctl.undo} onAbandon={ctl.askAbandon} />
    </>
  );
}

export function LapRun({ running, task, defs, ctl }) {
  useTick();
  const [adding, setAdding] = useState(false);
  const [extra, setExtra] = useState('');
  const state = lapsOf(running.marks);
  const kind = task.kind === 'routine' ? 'routine' : 'assembly';
  const known = (task.kind === 'routine'
    ? ((task.routine && task.routine.status === 'confirmed' && task.routine.steps) || []).map(s => ({ name: s.name }))
    : ((task.plan && task.plan.steps) || []).map(s => ({ name: s.name, item_id: s.item_id })));
  if (running.stopped) return <FinishForm running={running} task={task} defs={defs} busy={ctl.busy} onCarryOn={ctl.carryOn} onSave={ctl.saveLaps} />;
  const cur = state.laps[state.laps.length - 1];
  // Where in the list: the last listed step timed, so a step not on the list
  // does not lose the place.
  const at = state.laps.reduce((pos, l) => { const i = known.findIndex(k => k.name === l.name); return i >= 0 ? i : pos; }, -1);
  const next = known[at + 1] || null;
  const doneLaps = state.laps.slice(0, -1);
  return (
    <>
      {state.prep ? (
        <>
          <div style={{ textAlign: 'center', fontFamily: 'ui-monospace, monospace', fontSize: 44, fontWeight: 700, padding: '8px 0 0' }}>{clock(Date.now() - Date.parse(running.marks[0].at))}</div>
          <div style={{ textAlign: 'center', color: DIM, fontSize: 13, padding: '2px 16px 8px' }}>Getting ready</div>
          <button onClick={() => ctl.tap('start', known[0] ? { name: known[0].name, item_id: known[0].item_id } : {})} style={wide}>First part picked up{known[0] ? `: ${known[0].name}` : ''}</button>
        </>
      ) : (
        <>
          <div style={{ textAlign: 'center', fontFamily: 'ui-monospace, monospace', fontSize: 44, fontWeight: 700, padding: '8px 0 0', fontVariantNumeric: 'tabular-nums' }}>{clock(state.workSecs * 1000)}</div>
          <div style={{ textAlign: 'center', color: DIM, fontSize: 13, padding: '2px 16px 4px' }}>
            {state.rounds > 1 ? `Round ${state.rounds} · ` : ''}{cur && cur.name ? cur.name : `Step ${state.laps.length}`} · {secs(cur ? cur.secs : 0)}
          </div>
          <Def d={def(defs, kind, 'next')} />
          {!state.paused && (
            <div style={stepsBox}>
              {doneLaps.slice(-3).map((l, i) => (
                <div key={i} style={{ ...stepRow, color: DIM }}><span style={num}>✓</span>{l.name || `Step ${doneLaps.length - Math.min(3, doneLaps.length) + i + 1}`}<span style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, monospace', color: GREEN }}>{secs(l.secs)}</span></div>
              ))}
              {next && <button onClick={() => ctl.tap('next', { name: next.name, item_id: next.item_id })} style={nextStep}>Next: {next.name}</button>}
              {!known.length && <button onClick={() => ctl.tap('next')} style={nextStep}>Next step</button>}
              {known.length > 0 && at === known.length - 1 && (
                <button onClick={() => ctl.tap('next', { name: known[0].name, item_id: known[0].item_id, round: true })} style={{ ...stepRow, cursor: 'pointer', background: 'none', borderColor: TEAL, color: INK, fontWeight: 700 }}>
                  Next piece: back to {known[0].name}
                </button>
              )}
              {known.length > 0 && (adding ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input aria-label="The step not on the list" value={extra} onChange={e => setExtra(e.target.value)} placeholder="What is this step?" style={{ ...bigInput, flex: 1, fontSize: 15, padding: 9, fontFamily: 'system-ui, sans-serif' }} />
                  <button disabled={!extra.trim()} onClick={() => { ctl.tap('next', { name: extra.trim() }); setExtra(''); setAdding(false); }} style={smallBtn}>Go</button>
                  <button onClick={() => setAdding(false)} style={smallBtn}>✕</button>
                </div>
              ) : (
                <button onClick={() => setAdding(true)} style={{ ...stepRow, cursor: 'pointer', background: 'none', borderStyle: 'dashed', color: DIM }}>+ A step not on the list</button>
              ))}
            </div>
          )}
          <Pauser state={state} defs={defs} onTap={ctl.tap} />
          {!state.paused && <button onClick={ctl.stop} style={quiet}>Finish</button>}
        </>
      )}
      <Actions running={running} onUndo={ctl.undo} onAbandon={ctl.askAbandon} />
      {doneLaps.length > 3 && <div style={sect}>{doneLaps.length} steps timed so far</div>}
    </>
  );
}

// Time at the machine: one big button that says where you are.
export function AttentionRun({ running, defs, ctl }) {
  useTick();
  const marks = running.marks.filter(m => m.step === 'at' || m.step === 'away');
  const last = marks[marks.length - 1];
  const here = !!last && last.step === 'at';
  let spent = 0, since = null, visits = 0;
  for (const m of marks) {
    if (m.step === 'at') { since = Date.parse(m.at); visits++; } else if (since) { spent += Date.parse(m.at) - since; since = null; }
  }
  if (since) spent += Date.now() - since;
  const span = Date.now() - Date.parse(running.startedAt);
  return (
    <>
      <div style={{ textAlign: 'center', fontFamily: 'ui-monospace, monospace', fontSize: 44, fontWeight: 700, padding: '8px 0 0', fontVariantNumeric: 'tabular-nums' }}>{clock(span)}</div>
      <Def d={def(defs, 'attention', here ? 'away' : 'at')} />
      <button onClick={() => ctl.tap(here ? 'away' : 'at')} style={{ ...wide, padding: 26, fontSize: 18, background: here ? TEAL : 'none', color: here ? '#0b1215' : INK, border: here ? 'none' : `2px solid ${TEAL}` }}>
        {here ? 'At the machine · tap when you leave' : 'Away · tap when you go to the machine'}
      </button>
      <div style={{ textAlign: 'center', color: DIM, fontSize: 13 }}>
        {Math.round(spent / 60000)} min at the machine so far, in {visits} visit{visits === 1 ? '' : 's'}
      </div>
      <button disabled={ctl.busy} onClick={ctl.finishNow} style={quiet}>Finish{span < 3600000 ? ' (an hour is enough)' : ''}</button>
      <Actions running={running} onUndo={ctl.undo} onAbandon={ctl.askAbandon} />
    </>
  );
}
